import type { ReactNode } from "react";
import { useMemo } from "react";

import {
  buildVerdictFilterCounts,
  countRowsByVerdict,
  getScorecardRowsEmptyMessage,
  rowMatchesSearchQuery,
} from "../../utils";
import type { ScorecardAllRowsListState, ScorecardRowVerdictFilter } from "../../types";

import ScorecardRowFilterBar from "./ScorecardRowFilterBar";
import ScorecardRowResultsList from "./ScorecardRowResultsList";

import type { ScorecardRowSummary } from "@/queries/smart-tables/scorecard";
import { ScoreVerdict } from "@/queries/smart-tables/scorecard";

interface ScorecardAllRowsViewProps {
  titleSlot?: ReactNode;
  rows: ScorecardRowSummary[];
  /** Server-computed verdict counts across the whole calculation. */
  verdictCounts?: Record<string, number>;
  getRowLabel?: (rowIndex: number) => string;
  onOpenRow: (rowIndex: number) => void;
  onRerunRow: (rowIndex: number) => void;
  getIsRowRerunning: (rowIndex: number) => boolean;
  isRerunDisabled: boolean;
  onLoadMoreRows?: () => void;
  hasMoreRows?: boolean;
  isLoadingMoreRows?: boolean;
  listState: ScorecardAllRowsListState;
  onListStateChange: (next: Partial<ScorecardAllRowsListState>) => void;
}

const FILTER_OPTIONS: { id: ScorecardRowVerdictFilter; label: string; verdict?: string }[] = [
  { id: "all", label: "All" },
  { id: "fail", label: "Failed", verdict: ScoreVerdict.FAIL },
  { id: "warn", label: "Review", verdict: ScoreVerdict.WARN },
  { id: "pass", label: "Passed", verdict: ScoreVerdict.PASS },
];

const ScorecardAllRowsView = ({
  titleSlot,
  rows,
  verdictCounts,
  getRowLabel,
  onOpenRow,
  onRerunRow,
  getIsRowRerunning,
  isRerunDisabled,
  onLoadMoreRows,
  hasMoreRows = false,
  isLoadingMoreRows = false,
  listState,
  onListStateChange,
}: ScorecardAllRowsViewProps) => {
  const { search, filter } = listState;
  // Counts come from the server across the whole calculation; the loaded-rows
  // fallback only applies when the payload predates verdict_counts.
  const serverCounts = buildVerdictFilterCounts(verdictCounts);
  const counts = useMemo(() => {
    if (serverCounts) {
      return {
        all: serverCounts.all,
        pass: serverCounts.pass,
        warn: serverCounts.warn,
        fail: serverCounts.fail,
      };
    }
    return countRowsByVerdict(rows);
  }, [rows, serverCounts]);

  // Verdict filtering happens server-side (rows arrive pre-filtered); only
  // the label search is applied client-side over the loaded rows.
  const filteredRows = useMemo(
    () => rows.filter(row => rowMatchesSearchQuery(row.row_index, search, getRowLabel)),
    [getRowLabel, rows, search],
  );

  const rowResultItems = useMemo(
    () =>
      filteredRows.map(row => ({
        rowIndex: row.row_index,
        label: getRowLabel?.(row.row_index) ?? `Row ${row.row_index + 1}`,
        verdict: row.aggregate_verdict,
        score: row.aggregate_score,
        drift: row.drift_summary?.drift,
        errorMessage: row.error_summary ?? undefined,
        isStale: Boolean(row.stale_state?.is_stale),
      })),
    [filteredRows, getRowLabel],
  );

  return (
    <div className="space-y-3">
      {titleSlot}
      <ScorecardRowFilterBar
        search={search}
        onSearchChange={value => onListStateChange({ search: value })}
        filter={filter}
        onFilterChange={value => onListStateChange({ filter: value })}
        options={FILTER_OPTIONS}
        counts={counts}
      />

      <ScorecardRowResultsList
        items={rowResultItems}
        onOpenRow={onOpenRow}
        onRerunRow={onRerunRow}
        getIsRowRerunning={getIsRowRerunning}
        isRerunDisabled={isRerunDisabled}
        onLoadMore={onLoadMoreRows}
        hasMore={hasMoreRows}
        isLoadingMore={isLoadingMoreRows}
        emptyMessage={getScorecardRowsEmptyMessage(filter, search.trim().length > 0)}
      />
    </div>
  );
};

export default ScorecardAllRowsView;
